import { useEffect } from "react"
import { EventMap, useEditorEventBusInstance } from "../events/useEventBusInstance"
import { FlowsheetContent, SheetContent, SvgStreamDataState } from "../services/modal"
import { timeoutToPromise } from "../utils"
import { Edge } from "@antv/x6"
import { createSvgStream, createTargetSvgStream } from "../nodes"
import { useGraphInstance } from "@antv/xflow"
import { SvgStreamDirection } from "../nodes/SvgStream"
import { addConnectionToSheet, removeConnectionFromSheet } from "@/store/flowsheet/flowsheet.slice"
import { useDispatch } from "react-redux"

interface SvgStreamPluginProps {
  content: FlowsheetContent
  selected: SheetContent
  onContentChange: (content: FlowsheetContent) => void
}

interface AddOptions {
  edge: Edge
  sheetId: string
  endType: 'source' | 'target',
  streamDir: string
}

export const SvgStreamPlugin = (props: SvgStreamPluginProps) => {
  const {content, selected} = props;
  const graph = useGraphInstance()
  const eventBus = useEditorEventBusInstance()
  const dispatch = useDispatch()
  useEffect(() => {
    const deleteHandler = (data: SvgStreamDataState) => {
      const {from, to, streamId} = data;
      const sheetId = selected.id === from ? to : from
      const sheetIndex = content.sheets.findIndex(item => item.id === sheetId)
      const sheet = content.sheets[sheetIndex]
      if (!sheet) return;
      const index = (sheet.connections || []).findIndex(item => item.data.streamId === streamId)
      const connection = (sheet.connections || [])[index]
      if (!connection) return;

      const {ports} = connection
      const nozzles = ports.items
      const nozzle = nozzles[0]
      const nodeSize = connection.size;
      const position = connection.position
      const {x,y} = nozzle.layout
      const linePos = {
        x: position.x + nodeSize.width * x,
        y: position.y + nodeSize.height * y
      }
      const line = (sheet.lines || []).find(item => item.streamId === streamId || item.id === streamId)
      const params: any = {sheetId, id: connection.id};
      if (line) {
        const source: any = line.source;
        const target: any = line.target;

        if (source.cell === connection.id && nozzle.id === source.port) {
          params.line = {
            id: line.id,
            changes: {source: linePos}
          }
        } else if (target.cell === connection.id && nozzle.id === target.port) {
          params.line = {
            id: line.id,
            changes: {target: linePos}
          }
        }
      }
      dispatch(removeConnectionFromSheet(params))
    }

    const addHandler = async (addData: AddOptions) => {
      if (!graph) return;
      const {edge, endType, sheetId, streamDir} = addData
      const edgeData = edge.getData();
      const sheets = content.sheets;
      const sheet = sheets.find(sh => sh.id === sheetId)
      const data: SvgStreamDataState = {
        streamId: edge.id,
        from: selected!.id,
        to: sheetId,
        streamTag: `${sheet?.name}:${edgeData.tag}`
      }
      const streamDirection = Number(streamDir) as SvgStreamDirection
      
      const options = createSvgStream(edge, endType, streamDirection, data)
      const streamNode = graph.addNode(options)
      await timeoutToPromise(() => {
        const setter = endType === 'source' ? edge.setSource : edge.setTarget
        setter.apply(edge, [{
          cell: streamNode.id,
          port: options.ports.items[0].id
        }])
      }, 100)
      const targetConnection = createTargetSvgStream(streamDirection === SvgStreamDirection.In ? SvgStreamDirection.Out : SvgStreamDirection.In, {
        streamId: edge.data.streamId || edge.id,
        from: selected!.id,
        to: sheetId,
        streamTag: `${selected?.name}:${edgeData.tag}`
      })
      dispatch(addConnectionToSheet({sheetId, connection: targetConnection}))
    }
    eventBus.on(EventMap.deleteSvgStream, deleteHandler)
    eventBus.on(EventMap.addSvgStream, addHandler)

    return () => {
      eventBus.off(EventMap.deleteSvgStream, deleteHandler)
      eventBus.off(EventMap.addSvgStream, addHandler)
    }
  }, [content, selected, graph])

  return null
}